import { useState } from "react";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { useApproveDraft } from "@/api/mutations/repoLifecycle";
import { useUpdatePluginMeta } from "@/api/mutations/plugins";
import { usePlatformSettings } from "@/api/queries/platformSettings";
import { repoErrorMessage } from "@/components/repo-lifecycle/lifecycleVocab";

// #294 (UC-0, Arbens Fund): beim Freigeben eines PLUGIN-Entwurfs ist „Version" die
// PLUGIN-Version (Katalog-Eintrag = CCs natives Update-Signal) — nicht der Repo-Tag der
// Werkbank. Optional gesetzt wird sie VOR dem Merge in genau diesen Entwurf gepatcht.
// EIN Dialog für alle Plugin-Freigabe-Stellen (Arbeitsliste Aufnahme/Änderung,
// Detail-Kopf, Detail-Banner).
export function PluginApproveDialog({
  open,
  onOpenChange,
  marketplaceId,
  pluginName,
  repoId,
  branch,
  art,
  onApproved,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  marketplaceId: string;
  pluginName: string;
  /** Lifecycle-Koordinaten (Server-Zusage). */
  repoId: string;
  branch: string;
  art: "aufnahme" | "aenderung";
  onApproved?: () => void;
}) {
  const approve = useApproveDraft();
  const meta = useUpdatePluginMeta();
  // #294 (Review H2): mit aktivem Vier-Augen-Prinzip würde der Versions-Patch den
  // FREIGEBENDEN zum letzten Autor machen — der unmittelbar folgende approve liefe
  // zwingend in FOUR_EYES_REQUIRED und der Entwurf wäre für diese Person verbrannt.
  // Das Feld ist dann gesperrt; die Version setzt man vorher über „Meta bearbeiten"
  // (durch den Autor) oder eine zweite Person gibt frei.
  const settings = usePlatformSettings();
  const vierAugen = settings.data?.artifactApprovalRequiresSecondPerson === true;
  const [version, setVersion] = useState("");
  const busy = approve.isPending || meta.isPending;

  async function freigeben() {
    const mitVersion = !vierAugen && version.trim() !== "";
    // Review M3: die zwei Schritte GETRENNT behandeln — scheitert der Versions-Patch,
    // wurde nichts freigegeben, und die Meldung schickt an den richtigen Ort.
    if (mitVersion) {
      try {
        await meta.mutateAsync({ marketplaceId, pluginName, version: version.trim(), targetDraftBranch: branch });
      } catch (err) {
        toast.error(`Version konnte nicht gesetzt werden: ${repoErrorMessage(err, "Patch fehlgeschlagen")} — es wurde NICHTS freigegeben.`);
        return;
      }
    }
    try {
      await approve.mutateAsync({ repoId, branch });
      toast.success(
        art === "aufnahme"
          ? `„${pluginName}" freigegeben — jetzt im Katalog${mitVersion ? ` (Version ${version.trim()})` : ""}.`
          : `Änderung an „${pluginName}" freigegeben — jetzt live${mitVersion ? ` (Version ${version.trim()})` : ""}.`,
      );
      onOpenChange(false);
      onApproved?.();
    } catch (err) {
      toast.error(`${repoErrorMessage(err, "Freigeben fehlgeschlagen")} — in der Werkbank fortsetzen.`);
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="plugin-approve-dialog">
        <DialogHeader>
          <DialogTitle>{art === "aufnahme" ? `„${pluginName}" freigeben` : `Änderung an „${pluginName}" freigeben`}</DialogTitle>
          <DialogDescription>
            {art === "aufnahme"
              ? "Das Plugin wird Teil des Katalogs. Danach kann es freigeschaltet und Agents zugewiesen werden."
              : "Die Änderung geht live — Agents mit Auto-Update ziehen sie beim nächsten Start; alle anderen zeigen ‚Update verfügbar'."}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-1">
          <Label htmlFor="approve-version">Version (optional)</Label>
          <Input
            id="approve-version"
            value={version}
            onChange={(e) => setVersion(e.target.value)}
            placeholder="z.B. 1.2.0"
            maxLength={64}
            disabled={vierAugen}
            data-testid="approve-version"
          />
          <p className="text-[11.5px] text-text3">
            {vierAugen
              ? "Vier-Augen-Prinzip ist aktiv — die Version setzt der Autor vorher über ‚Meta bearbeiten' (der Freigebende darf den Entwurf nicht selbst ändern)."
              : "Die Plugin-Version ist das Update-Signal an die Agents — ohne Versions-Änderung wird nichts neu installiert."}
          </p>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={busy}>
            Abbrechen
          </Button>
          <Button onClick={() => void freigeben()} disabled={busy} data-testid="approve-confirm">
            Freigeben
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
